import { useEffect } from 'react';

import {
  roundADOTQuantity,
  toADOTQuantity,
} from '@atom/components/common/requests/adotQuantityUtilities';
import {
  formatADOTConversion,
  formatADOTQuantityWithUnit,
  getADOTPurchaseUnit,
  isADOTBaseUnit,
  multiplyADOTConversion,
} from '@atom/components/common/requests/adotReplenishmentUtilities';
import ADOTConvertedQuantity from '@atom/components/common/requests/viewRequest/adotView/ADOTConvertedQuantity';
import ADOTReplenishmentForm from '@atom/components/common/requests/viewRequest/adotView/ADOTReplenishmentForm';
import { Select, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import { AssetBudget, AssetDetail } from '@atom/types/asset';
import {
  AssetRequestCreateInput,
  ReplenishmentCreateInput,
} from '@atom/types/assetRequest';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { getADOTDefaultPurchaseUnit } from './adotCreateUtilities';

import classes from './adotCreate.module.css';

const { MenuItem } = Select;

const styles = {
  label: {
    color: colors.neutral.dim,
  },
  purchaseOrder: {
    maxWidth: '28.25rem',
  },
};

type ADOTReplenishmentCreateProps = {
  budget: AssetBudget;
  createInputs: AssetRequestCreateInput;
  updateCreateInput: (
    value: any,
    property: keyof AssetRequestCreateInput,
  ) => void;
  asset: AssetDetail;
};

const ADOTReplenishmentCreate = ({
  budget,
  createInputs,
  updateCreateInput,
  asset,
}: ADOTReplenishmentCreateProps) => {
  const conversionUnits = (budget?.conversionUnits ?? []).filter(
    unit => unit.active,
  );
  const purchaseUnit = getADOTPurchaseUnit(
    createInputs?.purchaseUnit,
    budget?.unit,
  );
  const showBase = !isADOTBaseUnit(purchaseUnit);

  useEffect(() => {
    if (createInputs?.purchaseUnit === undefined) {
      updateCreateInput(getADOTDefaultPurchaseUnit(budget), 'purchaseUnit');
    }
  }, []);

  const orderedBase = multiplyADOTConversion(
    createInputs?.quantityOrdered,
    purchaseUnit,
  );
  const receivedBase = multiplyADOTConversion(
    createInputs?.replenishment?.quantityReceived,
    purchaseUnit,
  );
  const remainingBase = isNilOrEmpty(orderedBase)
    ? null
    : roundADOTQuantity(orderedBase - (receivedBase ?? 0));

  const updateReplenishmentInput = (
    value: any,
    property: keyof ReplenishmentCreateInput,
  ) => {
    updateCreateInput(
      { ...createInputs.replenishment, [property]: value },
      'replenishment',
    );
  };

  const onPurchaseUnitChange = (unit: string) => {
    const selected = conversionUnits.find(option => option.unit === unit);

    updateCreateInput(
      selected
        ? { unit: selected.unit, conversionNumber: selected.conversionNumber }
        : null,
      'purchaseUnit',
    );
  };

  return (
    <>
      <div className={classes.viewRow}>
        <div className={classes.rowTitle}>Inventory Name</div>
        <div>{asset?.name}</div>
      </div>
      <div className={classes.inputContainer}>
        <TextField
          slotProps={{ inputLabel: { style: styles.label } }}
          style={styles.purchaseOrder}
          value={createInputs?.purchaseOrderNumber || ''}
          label="Purchase Order Number"
          onChange={event =>
            updateCreateInput(event.target.value, 'purchaseOrderNumber')
          }
          data-cy="adotReplenishmentPurchaseOrderNumber"
        />
      </div>
      <div className={classes.orderRow}>
        <TextField
          slotProps={{ inputLabel: { style: styles.label } }}
          type="number"
          value={createInputs?.quantityOrdered ?? ''}
          label="Total Qty Ordered"
          onChange={event =>
            updateCreateInput(
              toADOTQuantity(event.target.value),
              'quantityOrdered',
            )
          }
          helperText={
            showBase &&
            !isNilOrEmpty(orderedBase) &&
            formatADOTQuantityWithUnit(orderedBase, purchaseUnit.baseUnit)
          }
          data-cy="adotReplenishmentQuantityOrdered"
        />
        {conversionUnits.length ? (
          <Select
            label="Purchase unit"
            InputLabelProps={{ style: styles.label }}
            value={purchaseUnit.unit}
            onChange={event => onPurchaseUnitChange(event.target.value)}
            renderValue={(unit: string) => unit}
            helperText={showBase ? formatADOTConversion(purchaseUnit) : ''}
            data-cy="adotReplenishmentPurchaseUnitSelect"
          >
            {conversionUnits.map(option => (
              <MenuItem
                key={option.id}
                value={option.unit}
                data-cy={`adotReplenishmentPurchaseUnitOption-${option.unit}`}
              >
                <div>
                  <div>{option.unit}</div>
                  <div className={classes.assetOptionSubtext}>
                    {formatADOTConversion({
                      ...option,
                      baseUnit: purchaseUnit.baseUnit,
                    })}
                  </div>
                </div>
              </MenuItem>
            ))}
            <MenuItem
              value={purchaseUnit.baseUnit}
              data-cy={`adotReplenishmentPurchaseUnitOption-${purchaseUnit.baseUnit}`}
            >
              <div>
                <div>{purchaseUnit.baseUnit}</div>
                <div className={classes.assetOptionSubtext}>Base unit</div>
              </div>
            </MenuItem>
          </Select>
        ) : (
          <div>
            <div className={classes.rowTitle}>Purchase unit</div>
            <div data-cy="adotReplenishmentPurchaseUnit">{budget?.unit}</div>
          </div>
        )}
        <div>
          <div className={classes.rowTitle}>Total Qty Received</div>
          <ADOTConvertedQuantity
            baseValue={receivedBase}
            purchaseUnit={purchaseUnit}
            dataCy="adotReplenishmentTotalReceived"
          />
        </div>
        <div>
          <div className={classes.rowTitle}>Total Remaining Qty on Order</div>
          <ADOTConvertedQuantity
            baseValue={remainingBase}
            purchaseUnit={purchaseUnit}
            dataCy="adotReplenishmentTotalRemaining"
          />
        </div>
      </div>
      <div className={classes.inputContainer}>
        <ADOTReplenishmentForm
          title="Replenishment"
          purchaseUnit={purchaseUnit}
          totalOrderedBase={orderedBase}
          inputs={createInputs?.replenishment}
          updateInput={updateReplenishmentInput}
          dataCyPrefix="adotReplenishmentCreate"
        />
      </div>
    </>
  );
};

export default ADOTReplenishmentCreate;
